import { ErrorService, LoginErrorDetails, ServerErrorStatus } from '@agilox/common';
import { ButtonComponent } from '@agilox/ui';
import { UIThemes } from '@agilox/ui-common';
import { DOCUMENT } from '@angular/common';
import { Component, computed, inject, input, output } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { map, of, switchMap } from 'rxjs';

const SUPPORT_MAIL_HREF = 'mailto:support@agilox.net';
const SUPPORT_MAIL_LINE_SEPARATOR = '\n';
const UNSUPPORTED_ACCOUNT_ERROR_ID = 'AUTH-403-001';
const SupportMailKeys = {
	Subject: 'errorPage.support_mail.subject',
	Greeting: 'errorPage.support_mail.greeting',
	Intro: 'errorPage.support_mail.intro',
	Email: 'errorPage.support_mail.email',
	ErrorCode: 'errorPage.support_mail.error_code',
	ErrorId: 'errorPage.support_mail.error_id',
	Thanks: 'errorPage.support_mail.thanks',
	Closing: 'errorPage.support_mail.closing',
} as const;
const SUPPORT_MAIL_DETAIL_LINES = [
	['correlationId', 'errorPage.support_mail.correlation_id'],
	['traceId', 'errorPage.support_mail.trace_id'],
	['timestamp', 'errorPage.support_mail.timestamp'],
] as const;

enum ErrorType {
	SERVER = 'server',
	FORBIDDEN = 'forbidden',
	UNSUPPORTED_ACCOUNT = 'unsupported_account',
}

@Component({
	selector: 'error-page',
	templateUrl: './error-page.component.html',
	imports: [ButtonComponent, TranslateModule],
	host: { class: 'block' },
})
export class ErrorPageComponent {
	private document = inject(DOCUMENT);
	private errorService = inject(ErrorService);
	private translate = inject(TranslateService);

	status = input.required<ServerErrorStatus>();

	logout = output<void>();

	variant = computed<ErrorType>(() => {
		switch (this.status()) {
			case ServerErrorStatus.UNAUTHORIZED:
			case ServerErrorStatus.FORBIDDEN:
				return ErrorType.FORBIDDEN;
			case ServerErrorStatus.UNSUPPORTED_ACCOUNT:
				return ErrorType.UNSUPPORTED_ACCOUNT;
			default:
				return ErrorType.SERVER;
		}
	});

	code = computed(() => (this.variant() === ErrorType.SERVER ? '500' : '403'));

	loginErrorDetails = computed<LoginErrorDetails | null>(() =>
		this.variant() === ErrorType.UNSUPPORTED_ACCOUNT
			? (this.errorService.loginErrorDetails() ?? {})
			: null
	);

	supportHref = computed(() => `${SUPPORT_MAIL_HREF}${this.supportMailQuery() ?? ''}`);

	supportButtonType = computed(() =>
		this.variant() === ErrorType.UNSUPPORTED_ACCOUNT ? UIThemes.Primary : UIThemes.Secondary
	);

	private supportMailQuery = toSignal(
		toObservable(this.loginErrorDetails).pipe(
			switchMap((details) => (details ? this.translateSupportMail(details) : of(null)))
		)
	);

	retry(): void {
		this.document.defaultView?.location.reload();
	}

	private translateSupportMail(details: LoginErrorDetails) {
		const detailKeys = SUPPORT_MAIL_DETAIL_LINES.filter(([field]) => details[field]).map(
			([, key]) => key
		);
		return this.translate
			.stream([...Object.values(SupportMailKeys), ...detailKeys], {
				...details,
				errorCode: this.code(),
				errorId: UNSUPPORTED_ACCOUNT_ERROR_ID,
			})
			.pipe(
				map((translations: Record<string, string>) => {
					const body = [
						translations[SupportMailKeys.Greeting],
						'',
						translations[SupportMailKeys.Intro],
						'',
						translations[SupportMailKeys.Email],
						translations[SupportMailKeys.ErrorCode],
						translations[SupportMailKeys.ErrorId],
						...detailKeys.map((key) => translations[key]),
						'',
						translations[SupportMailKeys.Thanks],
						'',
						translations[SupportMailKeys.Closing],
					].join(SUPPORT_MAIL_LINE_SEPARATOR);
					const subject = translations[SupportMailKeys.Subject];
					return `?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
				})
			);
	}
}
